import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AppContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ProtectedRoute } from "./components/shared/ProtectedRoute";
import { WhatsAppButton } from "./components/shared/WhatsAppButton";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminDirectWork from "./pages/admin/AdminDirectWork";
import AdminDirectWorkNew from "./pages/admin/AdminDirectWorkNew";
import AdminDirectWorkThread from "./pages/admin/AdminDirectWorkThread";
import StudentDirectWork from "./pages/student/StudentDirectWork";
import StudentDirectWorkThread from "./pages/student/StudentDirectWorkThread";

import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import RoleSelect from "./pages/RoleSelect";
import StudentDashboard from "./pages/student/StudentDashboard";
import StudentOnboarding from "./pages/student/StudentOnboarding";
import FindTasks from "./pages/student/FindTasks";
import TaskDetails from "./pages/student/TaskDetails";
import CompanyDashboard from "./pages/company/CompanyDashboard";
import CompanyOnboarding from "./pages/company/CompanyOnboarding";
import PostTask from "./pages/company/PostTask";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTaskApproval from "./pages/admin/AdminTaskApproval";
import AdminAllocation from "./pages/admin/AdminAllocation";
import AdminKycReview from "./pages/admin/AdminKycReview";
import AdminGstReview from "./pages/admin/AdminGstReview";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import StudentApplications from "./pages/student/StudentApplications";
import CompanyMyTasks from "./pages/company/CompanyMyTasks";
import Disputes from "./pages/Disputes";
import NotificationsPage from "./pages/Notifications";
import Workroom from "./pages/shared/Workroom";

// Hash-based session_id detection runs BEFORE routes (prevents race conditions)
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/select-role" element={<ProtectedRoute><RoleSelect /></ProtectedRoute>} />

      {/* Student */}
      <Route path="/student/onboarding" element={<ProtectedRoute roles={["student"]}><StudentOnboarding /></ProtectedRoute>} />
      <Route path="/student" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/tasks" element={<ProtectedRoute roles={["student"]}><FindTasks /></ProtectedRoute>} />
      <Route path="/student/tasks/:id" element={<ProtectedRoute roles={["student"]}><TaskDetails /></ProtectedRoute>} />
      <Route path="/student/applications" element={<ProtectedRoute roles={["student"]}><StudentApplications /></ProtectedRoute>} />
      <Route path="/student/allocated" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/earnings" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/profile" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/notifications" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />

      {/* Company */}
      <Route path="/company/onboarding" element={<ProtectedRoute roles={["company"]}><CompanyOnboarding /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute roles={["company"]}><CompanyDashboard /></ProtectedRoute>} />
      <Route path="/company/post" element={<ProtectedRoute roles={["company"]}><PostTask /></ProtectedRoute>} />
      <Route path="/company/tasks" element={<ProtectedRoute roles={["company"]}><CompanyMyTasks /></ProtectedRoute>} />
      <Route path="/company/students" element={<ProtectedRoute roles={["company"]}><CompanyDashboard /></ProtectedRoute>} />
      <Route path="/company/payments" element={<ProtectedRoute roles={["company"]}><CompanyDashboard /></ProtectedRoute>} />
      <Route path="/company/profile" element={<ProtectedRoute roles={["company"]}><CompanyDashboard /></ProtectedRoute>} />
      <Route path="/company/notifications" element={<ProtectedRoute roles={["company"]}><CompanyDashboard /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/tasks" element={<ProtectedRoute roles={["admin"]}><AdminTaskApproval /></ProtectedRoute>} />
      <Route path="/admin/allocation" element={<ProtectedRoute roles={["admin"]}><AdminAllocation /></ProtectedRoute>} />
      <Route path="/admin/allocation/:taskId" element={<ProtectedRoute roles={["admin"]}><AdminAllocation /></ProtectedRoute>} />
      <Route path="/admin/students" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/kyc" element={<ProtectedRoute roles={["admin"]}><AdminKycReview /></ProtectedRoute>} />
      <Route path="/admin/gst" element={<ProtectedRoute roles={["admin"]}><AdminGstReview /></ProtectedRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute roles={["admin"]}><AdminAuditLog /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={["admin"]}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/direct-work" element={<ProtectedRoute roles={["admin"]}><AdminDirectWork /></ProtectedRoute>} />
      <Route path="/admin/direct-work/new" element={<ProtectedRoute roles={["admin"]}><AdminDirectWorkNew /></ProtectedRoute>} />
      <Route path="/admin/direct-work/:id" element={<ProtectedRoute roles={["admin"]}><AdminDirectWorkThread /></ProtectedRoute>} />
      <Route path="/student/direct-work" element={<ProtectedRoute roles={["student"]}><StudentDirectWork /></ProtectedRoute>} />
      <Route path="/student/direct-work/:id" element={<ProtectedRoute roles={["student"]}><StudentDirectWorkThread /></ProtectedRoute>} />

      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/disputes" element={<ProtectedRoute roles={["student", "company"]}><Disputes /></ProtectedRoute>} />
      <Route path="/admin/disputes" element={<ProtectedRoute roles={["admin"]}><Disputes adminView /></ProtectedRoute>} />

      <Route path="/workroom" element={<ProtectedRoute><Workroom /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-right"
      toastOptions={{
        style: {
          background: "var(--surface)",
          border: "1px solid var(--border-strong)",
          color: "var(--text)",
        },
      }}
    />
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <WhatsAppButton />
        </BrowserRouter>
        <ThemedToaster />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
